import { toSignal } from '@angular/core/rxjs-interop';
import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { map } from 'rxjs';
import { AjoutParametragesSpecifiquesEvenementRequetePayload, Evenement } from '../../../commissions.interface.';
import { CustomValidators } from '../../../../../../partage/forms/custom-validators';
import { normaliserSaisieMontant, normaliserSaisieNombre } from '../../../../../../partage/forms/normalisation-saisie';
import { nombreDepuisSaisie, nombreDepuisSaisieOuRepli } from '../../../../../../partage/format/nombre';
import { formaterDevise } from '../../../../../../partage/format/montant';
import { formaterPourcentage } from '../../../../../../partage/format/pourcentage';
import { CommissionsService } from '../../../commissions.service';
import { DatePipe } from '@angular/common';
import { ButtonComponent } from '@shared/components/button';
import { FieldErrorDirective, FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { PillComponent } from '@shared/components/pill';
import { SelectComponent } from '@shared/components/select';
import { TooltipDirective } from '@shared/directives/tooltip';

import type { SelectOption } from '@shared/components/dropdown/dropdown.types';

@Component({
  selector: 'app-ajouter-commission-evenement',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    DatePipe,
    ButtonComponent,
    FieldErrorDirective,
    FormFieldComponent,
    InputComponent,
    PillComponent,
    SelectComponent,
    TooltipDirective,
  ],
  templateUrl: './ajouter-commission-evenement.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './ajouter-commission-evenement.component.scss',
})
export class AjouterCommissionEvenementComponent {
  private commissionsService = inject(CommissionsService);

  // `InputType` du kit ne propose pas `number` : la saisie numérique passe par
  // `inputMode` + un filtre de caractères (voir design-system-manques.md, A14)
  protected readonly chiffres = /[0-9.,]/;
  evenementsSelectionnes = new FormControl<Evenement[]>([]);

  voirPanel = this.commissionsService.voirPanelAjoutParametrageSpecifiqueEvenement;
  evenements = this.commissionsService.evenements;

  reglesForm = new FormGroup({
    reglePourPlaces: new FormGroup({
      // Le POURCENTAGE est borné à 100, le montant ne l'est pas : en pratique il ne
      // dépasse jamais 100 € mais rien ne l'interdit. Les deux contraintes étaient
      // posées sur le montant, et aucune sur le pourcentage.
      // `null` et non `0` : « rien saisi » doit rester représentable. Initialisés à 0,
      // un simple focus + sortie du champ faisait apparaître l'aperçu — la
      // renormalisation réécrivait `0` (falsy) en `'0,00'` (truthy) sans aucune
      // saisie (review Gildas, MR !29). Le payload ne change pas :
      // `nombreDepuisSaisieOuRepli(null)` rend 0, et les validateurs laissent
      // passer le vide (`estVide`).
      pourcentage: new FormControl<number | null>(null, [
        CustomValidators.minNombre(0),
        CustomValidators.maxNombre(100),
      ]),
      minimum: new FormControl<number | null>(null, [CustomValidators.minNombre(0)]),
    }),
  });

  // Miroirs signal du formulaire de règle, lus par le gabarit et l'aperçu
  protected readonly regles = toSignal(this.reglesForm.valueChanges, { initialValue: this.reglesForm.value });
  protected readonly reglesValides = toSignal(this.reglesForm.statusChanges.pipe(map(() => this.reglesForm.valid)), {
    initialValue: this.reglesForm.valid,
  });

  constructor() {
    effect(() => {
      if (this.voirPanel()) {
        this.commissionsService.recupererLesEvenementsPourParametrage().subscribe();
      }
    });
  }

  // `app-select` raisonne en clés : l'identifiant d'évènement sert de clé, et les
  // états connus passent en suffixe d'option (Material les rendait en `mat-chip`)
  protected optionsEvenements = computed<SelectOption[]>(() =>
    (this.evenements() ?? []).map(evenement => {
      const etats = [
        evenement.estVerrouille ? 'acompte fait' : null,
        evenement.parametrageSpecifique ? 'déjà paramétré' : null,
      ].filter(Boolean);

      return {
        value: String(evenement.idEvenement),
        label: `${evenement.idAparte} - ${evenement.nomEvenement}`,
        ...(etats.length ? { suffix: etats.join(' · ') } : {}),
      };
    }),
  );

  /* Miroir signal de la selection. Un `computed` lisant `evenementsSelectionnes.value`
     ne se recalculait jamais — un `FormControl` n'est pas un signal : la liste
     deroulante ne recevait donc plus l'etat coche et une seule entree semblait
     selectionnable. */
  protected readonly evenementsSelectionnesClesSource = toSignal(this.evenementsSelectionnes.valueChanges, {
    initialValue: this.evenementsSelectionnes.value,
  });

  protected evenementsSelectionnesCles = computed<string[]>(() =>
    (this.evenementsSelectionnesClesSource() ?? []).map(evenement => String(evenement.idEvenement)),
  );

  protected choisirEvenements(cles: readonly string[]): void {
    const retenus = (this.evenements() ?? []).filter(evenement => cles.includes(String(evenement.idEvenement)));
    this.evenementsSelectionnes.setValue(retenus);
  }

  retirerEvenementDeLaSelection(event: Evenement) {
    const currentEvents = this.evenementsSelectionnes.value ?? [];
    const updatedEvents = currentEvents.filter(e => e.idEvenement !== event.idEvenement);
    this.evenementsSelectionnes.setValue(updatedEvents);
  }

  /* Renormalisation a la sortie du champ. Le TAUX ne doit pas etre arrondi (il part
     tel quel au serveur), le MONTANT est borne a deux decimales : deux fonctions. */
  protected readonly normaliserTaux = normaliserSaisieNombre;
  protected readonly normaliserMontant = normaliserSaisieMontant;

  /* Les valeurs viennent du formulaire, donc du kit, donc sous forme de CHAÎNES :
     `nombreDepuisSaisie` avant de formater, sans quoi `010` traverserait tel quel. */
  readonly reglePourPlacesLibelle = computed(() => {
    const regle = this.regles().reglePourPlaces;
    const minimum = formaterDevise(nombreDepuisSaisie(regle?.minimum));

    /* Test NUMERIQUE, pas truthy : le kit pousse des CHAINES et `"0"` est truthy —
       un taux saisi a 0 doit dire « (fixe) », comme la liste (`pourcentage > 0`). */
    if (nombreDepuisSaisieOuRepli(regle?.pourcentage) > 0) {
      const pourcentage = formaterPourcentage(nombreDepuisSaisie(regle?.pourcentage));
      return `Pour chaque place vendue, la commission web Tick&Live est de ${pourcentage} avec un minimum de ${minimum} `;
    }

    return `Pour chaque place vendue, la commission web Tick&Live est de ${minimum} (fixe)`;
  });

  fermerSlider() {
    this.voirPanel.set(false);
  }

  /* Un `FormControl` n'est pas un signal : sans ce miroir, la ligne d'erreur ne
     réapparaîtrait pas à la frappe. Le `Valider` se contente d'un `[disabled]`, une
     saisie hors bornes serait sinon refusée sans que rien ne l'explique. */
  private readonly pourcentageSaisi = toSignal(
    this.reglesForm.controls.reglePourPlaces.controls.pourcentage.valueChanges,
    {
      initialValue: this.reglesForm.controls.reglePourPlaces.controls.pourcentage.value,
    },
  );

  protected pourcentageHorsBornes = computed(() => {
    this.pourcentageSaisi();
    return this.reglesForm.controls.reglePourPlaces.controls.pourcentage.invalid;
  });

  valider() {
    if (this.reglesForm.valid && this.evenementsSelectionnes?.value?.length) {
      const evenementIds = this.evenementsSelectionnes.value.map(x => x.idEvenement);
      const { reglePourPlaces } = this.reglesForm.value;

      // pourcentage et minimum à 0 pour les produits car pas geré pour le moment
      const payload: AjoutParametragesSpecifiquesEvenementRequetePayload = {
        idsEvenements: evenementIds,
        reglePourPlaces: {
          minimum: nombreDepuisSaisieOuRepli(reglePourPlaces?.minimum),
          pourcentage: nombreDepuisSaisieOuRepli(reglePourPlaces?.pourcentage),
        },
        reglePourProduits: { minimum: 0, pourcentage: 0 },
      };

      this.commissionsService.parametrageSpecifiqueEvenementAAjouter.set(payload);
      this.fermerSlider();
    }
  }
}
